import React, { useEffect, useState } from 'react';
import { CommonButton } from 'components/Buttons';
import { DatePicker, TextInput } from 'components/Input';
import { KpiCategoryFilterConfiguration, KpiCategoryFilterValue } from 'models/request';
import kpiService from 'services/kpi.service';
import utils from 'utils';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import MultiSelectInput from 'components/Input/MultiSelect';
import TooltipControlled from 'components/Tooltip/tooltipControlled';
import { ActionsWrapper, FilterItem, Wrapper } from './styles';
import { buildDefaults, buildFilterValues, cleanDescendants, hasMandatoryFieldsFilled } from './helpers';

const delayShow = 400;

interface CategoryFiltersProps {
  categoryId: number;
  onFiltersChange: (filters: KpiCategoryFilterValue[]) => void;
  onReset?: (resetFn: () => void) => void;
  onConfigLoad?: (config: KpiCategoryFilterConfiguration[]) => void;
}

const CategoryFilters: React.FC<CategoryFiltersProps> = ({ categoryId, onFiltersChange, onReset, onConfigLoad }) => {
  const t = utils.intl.useTranslator();
  const dateFormat = useSelector(selectors.user.getUserPreference).dateFormat;
  const [config, setConfig] = useState<KpiCategoryFilterConfiguration[]>([]);
  const [pendingValues, setPendingValues] = useState<Record<string, any>>({});
  const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await kpiService.getCategoryFiltersConfiguration({ idCategory: categoryId });
        setConfig(data);
        onConfigLoad?.(data);
        const defaults = buildDefaults(data);
        setDefaultValues(defaults);
        setPendingValues(defaults);
        onReset?.(() => setPendingValues(defaults));
      } catch (error) {
        console.error('Failed to fetch category filter config:', error);
        setConfig([]);
      }
    };
    if (categoryId) fetchConfig();
  }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (filterCode: string, value: any) => {
    setPendingValues((prev) => {
      const next = { ...prev, [filterCode]: value };
      cleanDescendants(config, next, filterCode);
      return next;
    });
  };

  const applyFilters = () => onFiltersChange(buildFilterValues(config, pendingValues));

  const restoreDefaultFilters = () => {
    setPendingValues(defaultValues);
    onFiltersChange(buildFilterValues(config, defaultValues));
  };

  const renderFilterInput = (filter: KpiCategoryFilterConfiguration) => {
    switch (filter.filterType) {
      case 'text':
        return (
          <TextInput
            id={`filter-${filter.filterCode}`}
            value={pendingValues[filter.filterCode] ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(filter.filterCode, e.target.value)}
            placeholder={filter.label}
            width="200px"
          />
        );
      case 'selectOptions': {
        const parent = config.find((f) => f.subFilterLink === filter.filterCode);
        const parentValues: string[] = parent
          ? (pendingValues[parent.filterCode] ?? []).map((v: any) => v?.value ?? v)
          : [];
        const disabled = !!parent && parentValues.length === 0;
        const options = parent
          ? filter.options.filter((o) => parentValues.some((pv) => o.value.startsWith(`${pv}-`)))
          : filter.options;
        const selected = options.filter((o) =>
          (pendingValues[filter.filterCode] ?? []).some((v: any) => (v.value ?? v) === o.value),
        );
        return (
          <MultiSelectInput
            id={`filter-${filter.filterCode}`}
            labelledBy={filter.label}
            options={options}
            value={selected}
            onChange={(sel: any) => handleInputChange(filter.filterCode, sel)}
            placeholder={filter.label}
            disabled={disabled}
          />
        );
      }
      case 'dateRange':
        return (
          <DatePicker
            selected={
              pendingValues[filter.filterCode]?.[0]
                ? utils.date.convertToObjectDate(pendingValues[filter.filterCode][0])
                : null
            }
            selectedTo={
              pendingValues[filter.filterCode]?.[1]
                ? utils.date.convertToObjectDate(pendingValues[filter.filterCode][1])
                : null
            }
            onChange={(dates: Date | [Date | null, Date | null] | null) => {
              if (Array.isArray(dates)) {
                const [start, end] = dates;
                handleInputChange(filter.filterCode, [
                  start ? utils.date.formatDateForDb(start) : null,
                  end ? utils.date.formatDateForDb(end) : null,
                ]);
              }
            }}
            dateFormat={dateFormat}
            isRange
            isClearable
            inputWidth="220px"
          />
        );
      default:
        return null;
    }
  };

  if (!config.length) return null;

  return (
    <Wrapper>
      {config.map((filter) => (
        <FilterItem key={filter.filterCode}>{renderFilterInput(filter)}</FilterItem>
      ))}
      <ActionsWrapper>
        <TooltipControlled
          content={utils.app.textTranslated('Refresh-filters', t, 'Refresh filters')}
          trigger="hover"
          placement="bottom"
          delayShow={delayShow}
        >
          <CommonButton
            id="ct-category-refresh-filters"
            scope="primary"
            action={applyFilters}
            disabled={!hasMandatoryFieldsFilled(config, pendingValues)}
            customIcon="search"
          />
        </TooltipControlled>
        <TooltipControlled
          content={utils.app.textTranslated('Default-filters', t, 'Default filters')}
          trigger="hover"
          placement="bottom"
          delayShow={delayShow}
        >
          <CommonButton
            id="ct-category-restore-default-filters"
            scope="secondary"
            action={restoreDefaultFilters}
            customIcon="undo-alt"
          />
        </TooltipControlled>
      </ActionsWrapper>
    </Wrapper>
  );
};

export default CategoryFilters;
